import { useEffect, useRef, useState, useCallback } from 'react';
import { parseQuestionsRealtime, ParsedQuestionFromAPI } from '../utils/simulationApi';
import { ParsedQuestion } from '../components/survey/types';

const DEBOUNCE_MS = 1200;

export interface UseRealtimeParsingResult {
  parsedQuestions: ParsedQuestion[];
  /** True during the very first parse when no cached results exist yet. */
  isParsing: boolean;
  /** True during a re-parse while stale (but valid) results are still visible. */
  isUpdating: boolean;
  /** Feed user edits (type changes, images) back into the cache so they survive re-parses. */
  seedCache: (questions: ParsedQuestion[]) => void;
  /** Hard-reset the hook: clear cache + results (used by "Back to Questions"). */
  reset: () => void;
}

function normalizeText(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

function mapApiQuestion(q: ParsedQuestionFromAPI, fallbackId: string): ParsedQuestion {
  return {
    id: q.id || fallbackId,
    text: q.text,
    type: q.type,
    choices: q.choices,
    scale_min: q.scale_min,
    scale_max: q.scale_max,
    max_selections: q.max_selections,
    ranking_count: q.ranking_count,
    text_limit: q.text_limit,
    image_paths: q.image_paths,
    image_descriptions: q.image_descriptions,
    image_attachment_level: q.image_attachment_level as 'question' | 'option' | undefined,
    option_images: q.option_images,
    maxdiff_items: q.maxdiff_items,
    conjoint_attributes: q.conjoint_attributes,
    conjoint_description: q.conjoint_description,
  };
}

/** Merge user edits from a cached question onto a fresh API result.
 *
 * - Fields from **parsing** (choices, matrix data) always use fresh —
 *   they are re-extracted from the latest textarea text each time.
 *   `null` from the API means "no choices" (user removed them), NOT
 *   "unknown"; we must NOT fall back to stale cached choices.
 * - Fields from **user intent** (type, images, scale config) prefer
 *   cached — the user explicitly set them in the preview.
 */
function mergeWithCached(fresh: ParsedQuestion, cached: ParsedQuestion): ParsedQuestion {
  return {
    ...fresh,
    // User-intent fields: prefer cached
    type: cached.type,
    scale_min: cached.scale_min ?? fresh.scale_min,
    scale_max: cached.scale_max ?? fresh.scale_max,
    max_selections: cached.max_selections ?? fresh.max_selections,
    ranking_count: cached.ranking_count ?? fresh.ranking_count,
    text_limit: cached.text_limit ?? fresh.text_limit,
    image_paths: cached.image_paths?.length ? cached.image_paths : fresh.image_paths,
    image_descriptions: cached.image_descriptions?.length ? cached.image_descriptions : fresh.image_descriptions,
    image_attachment_level: cached.image_attachment_level ?? fresh.image_attachment_level,
    option_images: cached.option_images && Object.keys(cached.option_images).length > 0
      ? cached.option_images : fresh.option_images,
    // Parsed-from-text fields: always use fresh (no fallback to stale cache)
    choices: fresh.choices,
    maxdiff_items: fresh.maxdiff_items,
    conjoint_attributes: fresh.conjoint_attributes,
    conjoint_description: fresh.conjoint_description,
  };
}

/**
 * Dual-key cache: stores ParsedQuestion objects keyed by both question ID
 * and normalized text. ID is the primary lookup; text is a fallback when
 * the AI re-assigns IDs.
 */
class QuestionCache {
  private byId = new Map<string, ParsedQuestion>();
  private byText = new Map<string, ParsedQuestion>();

  set(q: ParsedQuestion) {
    this.byId.set(q.id, q);
    this.byText.set(normalizeText(q.text), q);
  }

  /** Look up by ID first, then fall back to normalized text. */
  get(id: string, text: string): ParsedQuestion | undefined {
    return this.byId.get(id) ?? this.byText.get(normalizeText(text));
  }

  /** Remove entries whose IDs are NOT in the given set (stale trim). */
  retainIds(ids: Set<string>) {
    for (const [key, q] of this.byId) {
      if (!ids.has(key)) {
        this.byId.delete(key);
        this.byText.delete(normalizeText(q.text));
      }
    }
  }

  clear() {
    this.byId.clear();
    this.byText.clear();
  }

  get size() { return this.byId.size; }
}

/**
 * Realtime question parsing hook with dual-key caching.
 *
 * - Debounces textarea input, sends text to backend for Haiku tagging
 * - Dual-key cache (ID + text) preserves user edits for unchanged questions
 * - Never clears visible results mid-parse; shows stale data with `isUpdating` flag
 * - Append-only optimisation: only sends the new tail when user appends text
 */
export default function useRealtimeQuestionTags(
  questionsText: string,
  goal: string,
  context: string,
  enabled: boolean = true,
): UseRealtimeParsingResult {
  const [parsedQuestions, setParsedQuestions] = useState<ParsedQuestion[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const lastParsedTextRef = useRef<string>('');
  const lastResultRef = useRef<ParsedQuestion[]>([]);
  const cacheRef = useRef(new QuestionCache());

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
  }, []);

  const seedCache = useCallback((questions: ParsedQuestion[]) => {
    const cache = cacheRef.current;
    for (const q of questions) {
      cache.set(q);
    }
    lastResultRef.current = questions;
  }, []);

  const reset = useCallback(() => {
    cancel();
    setParsedQuestions([]);
    setIsParsing(false);
    setIsUpdating(false);
    lastParsedTextRef.current = '';
    lastResultRef.current = [];
    cacheRef.current.clear();
  }, [cancel]);

  useEffect(() => {
    if (!enabled) {
      cancel();
      setIsUpdating(false);
      return;
    }

    const trimmed = questionsText.trim();

    if (!trimmed) {
      cancel();
      setParsedQuestions([]);
      setIsParsing(false);
      setIsUpdating(false);
      lastParsedTextRef.current = '';
      lastResultRef.current = [];
      cacheRef.current.clear();
      return;
    }

    if (trimmed === lastParsedTextRef.current) {
      return;
    }

    cancel();

    const hasExisting = lastResultRef.current.length > 0;
    if (hasExisting) {
      setIsUpdating(true);
    }

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      if (!hasExisting) {
        setIsParsing(true);
      }

      try {
        const lastText = lastParsedTextRef.current;
        const isAppendOnly = lastText.length > 0 && trimmed.startsWith(lastText);

        let textToSend: string;
        if (isAppendOnly) {
          textToSend = trimmed.substring(lastText.length);
          if (!textToSend.trim()) {
            setIsParsing(false);
            setIsUpdating(false);
            return;
          }
        } else {
          textToSend = trimmed;
        }

        const result = await parseQuestionsRealtime(
          textToSend, goal, context, controller.signal,
        );
        if (controller.signal.aborted) return;

        const apiQuestions = result.questions.map(
          (q: ParsedQuestionFromAPI, i: number) => mapApiQuestion(q, `Q${i + 1}`),
        );

        let finalQuestions: ParsedQuestion[];
        const cache = cacheRef.current;

        if (isAppendOnly) {
          const startNum = lastResultRef.current.length + 1;
          const renumbered = apiQuestions.map((q, i) => ({
            ...q,
            id: `Q${startNum + i}`,
          }));
          finalQuestions = [...lastResultRef.current, ...renumbered];
        } else {
          // Detect full replacement: if old and new text share very little
          // content (prefix + suffix), the user replaced everything (select-
          // all + paste). Clear cache so stale Q1/Q2 IDs don't collide
          // with completely different new questions.
          if (lastText.length > 0) {
            const minLen = Math.min(lastText.length, trimmed.length);
            let prefixLen = 0;
            while (prefixLen < minLen && lastText[prefixLen] === trimmed[prefixLen]) {
              prefixLen++;
            }
            let suffixLen = 0;
            const maxSuffix = minLen - prefixLen;
            while (
              suffixLen < maxSuffix &&
              lastText[lastText.length - 1 - suffixLen] === trimmed[trimmed.length - 1 - suffixLen]
            ) {
              suffixLen++;
            }
            const sharedLen = prefixLen + suffixLen;
            if (sharedLen < lastText.length * 0.3) {
              cache.clear();
            }
          }

          finalQuestions = apiQuestions.map((q, i) => {
            const id = q.id || `Q${i + 1}`;
            const cached = cache.get(id, q.text);
            if (cached) {
              return mergeWithCached({ ...q, id }, cached);
            }
            return { ...q, id };
          });
        }

        // Update cache with final results (don't clear first — preserve entries)
        const activeIds = new Set(finalQuestions.map(q => q.id));
        for (const q of finalQuestions) {
          cache.set(q);
        }
        // Trim stale entries no longer in the result set
        cache.retainIds(activeIds);

        lastParsedTextRef.current = trimmed;
        lastResultRef.current = finalQuestions;
        setParsedQuestions(finalQuestions);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        console.warn('Realtime question parsing failed:', err);
      } finally {
        if (!controller.signal.aborted) {
          setIsParsing(false);
          setIsUpdating(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => cancel();
  }, [questionsText, goal, context, enabled, cancel]);

  useEffect(() => {
    return () => cancel();
  }, [cancel]);

  return { parsedQuestions, isParsing, isUpdating, seedCache, reset };
}
